// src/utils/wallet.js

export const WORLDLAND_NETWORKS = {
  mainnet: {
    chainId: '0x67', // 103 in hex
    chainName: 'WorldLand Seoul',
    nativeCurrency: {
      name: 'WLC',
      symbol: 'WLC',
      decimals: 18
    },
    rpcUrls: ['https://seoul.worldland.foundation/'],
    blockExplorerUrls: ['https://scan.worldland.foundation/']
  },
  testnet: {
    chainId: '0x289B', // 10395 in hex
    chainName: 'WorldLand Gwangju',
    nativeCurrency: {
      name: 'WLC',
      symbol: 'WLC',
      decimals: 18
    },
    rpcUrls: ['https://gwangju.worldland.foundation/'],
    blockExplorerUrls: ['https://scan.worldland.foundation/']
  },
  sepolia: {
    chainId: '0xaa36a7', // 11155111 in hex
    chainName: 'Sepolia test network',
    nativeCurrency: {
      name: 'SepoliaETH',
      symbol: 'ETH',
      decimals: 18
    },
    rpcUrls: ['https://sepolia.infura.io/v3/'],
    blockExplorerUrls: ['https://sepolia.etherscan.io/']
  }
};

// MetaMask 프로바이더 가져오기
export const getMetaMaskProvider = () => {
  if (typeof window === 'undefined') return null;
  
  // 다중 지갑이 설치된 경우 providers 배열에서 MetaMask 찾기
  if (window.ethereum?.providers && Array.isArray(window.ethereum.providers)) {
    const metaMaskProvider = window.ethereum.providers.find(provider => {
      try {
        return provider && provider.isMetaMask === true;
      } catch (error) {
        console.log('Provider check error:', error);
        return false;
      }
    });
    
    if (metaMaskProvider) {
      return metaMaskProvider;
    }
  }
  
  // MetaMask가 유일한 지갑인 경우
  if (window.ethereum && window.ethereum.isMetaMask === true) {
    return window.ethereum;
  }
  
  // 백업: window.ethereum이 있지만 isMetaMask 속성이 없는 경우
  if (window.ethereum && !window.ethereum.providers) {
    // request 메서드가 있는지 확인
    if (typeof window.ethereum.request === 'function') {
      return window.ethereum;
    }
  }
  
  return null;
};

// 메타마스크 설치 확인
export const isMetaMaskInstalled = () => {
  return getMetaMaskProvider() !== null;
};

// 지갑 설치 확인 (MetaMask 전용)
export const isWalletInstalled = () => {
  return isMetaMaskInstalled();
};

// MetaMask로 연결
export const connectWallet = async () => {
  const provider = getMetaMaskProvider();
  
  if (!provider) {
    throw new Error('MetaMask가 설치되지 않았습니다. https://metamask.io에서 설치해주세요.');
  }

  // provider에 request 메서드가 있는지 확인
  if (typeof provider.request !== 'function') {
    throw new Error('MetaMask 프로바이더에 오류가 있습니다. 페이지를 새로고침해주세요.');
  }

  try {
    const accounts = await provider.request({
      method: 'eth_requestAccounts'
    });
    
    if (!accounts || accounts.length === 0) {
      throw new Error('계정을 찾을 수 없습니다.');
    }
    
    return accounts[0];
  } catch (error) {
    console.error('ConnectWallet error:', error);
    
    if (error.code === 4001) {
      throw new Error('MetaMask 연결이 거부되었습니다.');
    }
    if (error.code === -32002) {
      throw new Error('MetaMask에서 이미 연결 요청을 처리 중입니다. MetaMask를 확인해주세요.');
    }
    throw new Error('MetaMask 연결에 실패했습니다: ' + error.message);
  }
};

// 현재 네트워크 확인 (MetaMask 전용)
export const getCurrentNetwork = async () => {
  const provider = getMetaMaskProvider();
  
  if (!provider) {
    return null;
  }

  // provider에 request 메서드가 있는지 확인
  if (typeof provider.request !== 'function') {
    console.error('Provider request method not available');
    return null;
  }

  try {
    const chainId = await provider.request({ method: 'eth_chainId' });
    return chainId;
  } catch (error) {
    console.error('네트워크 확인 실패:', error);
    return null;
  }
};

// 월드랜드 네트워크인지 확인
export const isWorldLandNetwork = async () => {
  try {
    const currentChainId = await getCurrentNetwork();
    if (!currentChainId) return false;
    
    return currentChainId === WORLDLAND_NETWORKS.mainnet.chainId || 
           currentChainId === WORLDLAND_NETWORKS.testnet.chainId ||
           currentChainId === WORLDLAND_NETWORKS.sepolia.chainId;
  } catch (error) {
    console.error('isWorldLandNetwork error:', error);
    return false;
  }
};

// 현재 네트워크가 테스트넷인지 확인
export const isTestnetNetwork = async () => {
  try {
    const currentChainId = await getCurrentNetwork();
    return currentChainId === WORLDLAND_NETWORKS.testnet.chainId ||
           currentChainId === WORLDLAND_NETWORKS.sepolia.chainId;
  } catch (error) {
    console.error('isTestnetNetwork error:', error);
    return false;
  }
};

// 네트워크 정보 가져오기
export const getNetworkInfo = async () => {
  try {
    const currentChainId = await getCurrentNetwork();
    
    if (currentChainId === WORLDLAND_NETWORKS.mainnet.chainId) {
      return { ...WORLDLAND_NETWORKS.mainnet, isTestnet: false };
    } else if (currentChainId === WORLDLAND_NETWORKS.testnet.chainId) {
      return { ...WORLDLAND_NETWORKS.testnet, isTestnet: true };
    } else if (currentChainId === WORLDLAND_NETWORKS.sepolia.chainId) {
      return { ...WORLDLAND_NETWORKS.sepolia, isTestnet: true };
    }
    
    return null;
  } catch (error) {
    console.error('getNetworkInfo error:', error);
    return null;
  }
};

// 월드랜드 네트워크 추가/전환
export const addWorldLandNetwork = async (networkType = 'testnet') => {
  const provider = getMetaMaskProvider();
  
  if (!provider) {
    throw new Error('MetaMask가 설치되지 않았습니다.');
  }

  // provider에 request 메서드가 있는지 확인
  if (typeof provider.request !== 'function') {
    throw new Error('MetaMask 프로바이더에 오류가 있습니다. 페이지를 새로고침해주세요.');
  }

  let network;
  switch (networkType) {
    case 'mainnet':
      network = WORLDLAND_NETWORKS.mainnet;
      break;
    case 'testnet':
      network = WORLDLAND_NETWORKS.testnet;
      break;
    case 'sepolia':
      network = WORLDLAND_NETWORKS.sepolia;
      break;
    default:
      network = WORLDLAND_NETWORKS.testnet;
  }

  try {
    // 먼저 네트워크 전환 시도
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: network.chainId }]
    });
    
    return true;
  } catch (switchError) {
    // 네트워크가 추가되지 않은 경우 추가 (Sepolia는 이미 있을 가능성이 높음)
    if (switchError.code === 4902 && networkType !== 'sepolia') {
      try {
        await provider.request({
          method: 'wallet_addEthereumChain',
          params: [network]
        });
        
        return true;
      } catch (addError) {
        console.error('네트워크 추가 실패:', addError);
        
        if (addError.code === 4001) {
          throw new Error('네트워크 추가가 거부되었습니다.');
        }
        throw new Error('네트워크 추가에 실패했습니다: ' + addError.message);
      }
    } else {
      console.error('네트워크 전환 실패:', switchError);
      
      if (switchError.code === 4001) {
        throw new Error('네트워크 전환이 거부되었습니다.');
      }
      throw new Error('네트워크 전환에 실패했습니다: ' + switchError.message);
    }
  }
};

// WLC 잔액 조회 (MetaMask 전용)
export const getWLCBalance = async (address) => {
  const provider = getMetaMaskProvider();
  
  if (!provider) {
    return '0';
  }

  // provider에 request 메서드가 있는지 확인
  if (typeof provider.request !== 'function') {
    console.error('Provider request method not available');
    return '0';
  }

  try {
    const balance = await provider.request({
      method: 'eth_getBalance',
      params: [address, 'latest']
    });
    
    // Wei를 WLC로 변환 (18 decimals)
    const balanceInWLC = parseInt(balance, 16) / Math.pow(10, 18);
    return balanceInWLC.toFixed(4);
  } catch (error) {
    console.error('잔액 조회 실패:', error);
    return '0';
  }
};